<template>
  <div class="bg-light-grey-600 notification__popup" :class="popupDynamicClass">
    <div class="gp_modal-close p-1 notification__close" @click="closePopup">
      <i class="icon icon-small-cancel"></i>
    </div>
    <div v-if="notification.img_url && notification.img_url != ''" class="notification__img">
      <img :src="notification.img_url" id="noti-img">
      <div v-if="isVideoUrl" id="video-overlay">
        <svg width="48" height="48" viewBox="0 0 48 48" fill="none">
          <path
            d="M24 4C12.95 4 4 12.95 4 24C4 35.05 12.95 44 24 44C35.05 44 44 35.05 44 24C44 12.95 35.05 4 24 4ZM20 33V15L32 24L20 33Z"
            fill="#fff"
          />
        </svg>
      </div>
      <video v-if="isVideoUrl" id="video" :src="notification.settings.video_url" controls="controls"></video>
    </div>
    <div :class="containerDynamicClass">
      <div class="notification__tag" v-if="notificationTag">{{ notificationTag }}</div>
      <h2
        class="heading-h2-24-semi"
        v-html="title"
        :class="titleDynamicClass"
      ></h2>
      <p
        class="body-14-regular text-dark-subtext2 mt-4px mb-2"
        :class="timeDynamicClass"
        v-if="isShowTimeAgo"
      >
        {{ timeago }}
      </p>
      <div v-if="isWelcomePopup">
        <p class="paragraph-14-regular txt-dark-high-contrast mb-2 mt-3">
          {{ $t("pricing.welcomePopupContent") }}
        </p>
        <a
          class="txt-primary-300 txt-primary-200-hover"
          target="_blank"
          href="https://help.gempages.net/release-note"
        >
          {{ $t("pricing.releaseNoteLink") }}
        </a>
      </div>
      <div v-else v-html="notiContent"></div>
    </div>
  </div>
</template>

<script>
import getLocale from "@/mixins/getLocale.js";
import { mapGetters, mapMutations} from "vuex";
import { mutations } from "@/store/page/types";
import { TimeMaker } from "@/plugins";
import { createThemeAndPageV7 } from "@/modules/Instant/utils";

export default {
  name: "NotificationPopup",
  mixins: [getLocale],
  props: {
    notification: {
      type: Object,
      default: null,
    },
  },
  computed: {
    ...mapGetters("account", {
      language: "displayLanguage",
    }),
    title() {
      return this.getLocaleText(this.notification.title);
    },
    timeago() {
      return new TimeMaker(this.notification.base_created).ago(this.language);
    },
    hasTags() {
      return this.notification.is_important;
    },
    timeDynamicClass() {
      return {};
    },
    notiContent() {
      return this.getLocaleText(this.notification.content);
    },
    isWelcomePopup() {
      return this.notification.action == "welcome_back";
    },
    isExtendTrial() {
      return !!this.notification.settings["extend-trial"];
    },
    popupDynamicClass() {
      return {
        "extend-trial": this.isExtendTrial,
      };
    },
    containerDynamicClass() {
      return {
        "p-4": !this.isExtendTrial,
        "p-4 pb-5": this.isExtendTrial,
      };
    },
    titleDynamicClass() {
      return {
        "mb-2": this.isExtendTrial,
        "color-red-300": this.notification.settings["extend-trial"] <= 3 && this.isExtendTrial
      };
    },
    isVideoUrl() {
      const videoUrl = this.notification.settings?.video_url;
      return videoUrl && !["false", ""].includes(videoUrl);
    },
    notificationData() {
      return this.notification.settings.data && JSON.parse(this.notification.settings.data);
    },
    notificationTag() {
      return this.notificationData?.tag;
    },
    isShowTimeAgo() {
      const hideTimeAgo = this.notificationData?.hideTimeAgo;
      return hideTimeAgo
        ? !hideTimeAgo
        : !this.isWelcomePopup && !this.isExtendTrial;
    }
  },
  mounted() {
    this.handleCreateNewPage();

    if (this.notification.settings.video_url) {
      this.handlePlayVideo()
    }
  },
  methods: {
    ...mapMutations("page", {
      setFrameLoading: mutations.SET_FRAME_LOADING
    }),
    closePopup() {
      this.$emit("close");
    },
    async onCreateNewPage() {
      try {
        this.setFrameLoading({ isLoading: true, typeLoading: "create_new_page" });
        await createThemeAndPageV7({ pageType: 'subpages', createByGenerateImage: true });
      } catch {
        this.setFrameLoading({ isLoading: false, typeLoading: "create_new_page" });
      }
    },
    handleCreateNewPage() {
      const createPageV7Button = document.getElementById('create-new-page-from-noti');
      if (createPageV7Button) {
        createPageV7Button.addEventListener('click', () => this.onCreateNewPage());
      }
    },
    handlePlayVideo() {
      const video = document.getElementById('video');
      const image = document.getElementById('noti-img');
      const overlay = document.getElementById('video-overlay');
      
      if(!video || !overlay || !image) return;
      
      overlay.addEventListener('click', (event) => {
        image.style.display = 'none';
        overlay.style.display = 'none';

        video.style.position = 'static';
        video.play();
        
        event.preventDefault();
      });
    }
  },
};
</script>

<style lang="scss">
$accent-red-300: #f44336;

#video {
  position: absolute;
  width: 100%;
  left: 0;
  z-index: -1;
}

#video-overlay {
  position: absolute;
  top: 0;
  width: 100%;
  height: 100%;
  display: flex;
  align-items: center;
  justify-content: center;
  cursor: pointer;
  transition: background-color 0.5s ease;
}

.notification {
  &__popup {
    position: relative;
    max-width: 600px;
    min-height: 100%;

    &.extend-trial {
      max-width: 480px;
    }
  }
  &__upgrade {
    &-btn {
      width: 100%;
      &:hover {
        background-color: map-get($primary, "200");
      }
    }
  }
  &__tag {
    width: fit-content;
    margin-bottom: 8px;
    display: flex;
    height: 20px;
    padding: 0px 4px;
    justify-content: center;
    align-items: center;
    border-radius: 4px;
    background: #D8E1FF;
    color: #0A3BE7;
    font-size: 12px;
    font-family: Inter;
    font-style: normal;
    font-weight: 500;
    line-height: 20px;
  }
  &__close {
    background-color: $light2;
    .icon {
      color: $textLightModeHigh;
      font-size: 16px;
    }
  }
}

.color {
  &-red-300 {
    color: $accent-red-300;
  }
}
</style>
